import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  collection,
  onSnapshot,
  deleteDoc,
  doc,
  getDocs,
  setDoc,
  getDoc,
  updateDoc,
  addDoc
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../AuthProvider';
import PageWrapper from '../components/PageWrapper';
import { useUI } from '../UIContext';
import { useNavigate } from 'react-router-dom';
import BorderedBox from '../components/BorderedBox';
import TuneCardBox from '../components/TuneCardBox';
import StyledCheckbox from '../components/StyledCheckbox';
import myIcon from '../assets/logo512.png'; // adjust path
import { normalizeGenre, normalizeType, keyRoot } from '../utils/normalize';
import CustomTagInput from '../components/CustomTagInput';
import { useUserPrefs } from '../UserPrefsProvider';
import TagFilterControl from '../components/TagFilterControl';
import dexie from '../offlineDB';
import CreateIrishSetForm from '../components/CreateIrishSetForm'; // adjust path if needed

export default function SetsPage() {
  const [collapsed, setCollapsed] = useState(false);
const boxRef = useRef(null);
const [scrollHeight, setScrollHeight] = useState(300);

  const { user } = useAuth();
  const { inputStyle, textStyle } = useUI();
  const [sets, setSets] = useState([]);
  const [tunes, setTunes] = useState([]);

  const [selectedSetIds, setSelectedSetIds] = useState([]);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [undoTimer, setUndoTimer] = useState(null);
  const navigate = useNavigate();

  // New filter state
  const [filterSetQuery, setFilterSetQuery] = useState('');
  const [filterSetRating, setFilterSetRating] = useState('');
  const [filterSetLabel, setFilterSetLabel] = useState('');
  const [labelOptions, setLabelOptions] = useState([]);
  const [filterSetGenre, setFilterSetGenre] = useState('');
const [filterSetType, setFilterSetType] = useState('');
const [selectedSetTags, setSelectedSetTags] = useState([]);
const [setTagLogic, setSetTagLogic] = useState('AND');
const { prefs } = useUserPrefs();
const [showSetTagFilters, setShowSetTagFilters] = useState(false);
const [sortOrder, setSortOrder] = useState('newest');
const [bulkSetAction, setBulkSetAction] = useState('');
const [showBulkSetRating, setShowBulkSetRating] = useState(false);
const [bulkRatingValue, setBulkRatingValue] = useState('');
const [bulkActionValue, setBulkActionValue] = useState('');
const [undoQueue, setUndoQueue] = useState([]);
const [snackbarMessage, setSnackbarMessage] = useState('');
const [showSnackbar, setShowSnackbar] = useState(false);
const [showBulkTagInput, setShowBulkTagInput] = useState(false);
const [bulkTags, setBulkTags] = useState([]);
const [showIrishForm, setShowIrishForm] = useState(false);

useEffect(() => {
  if (!user) return;

  if (!navigator.onLine) {
    dexie.tunes.toArray().then(localTunes => {
      setTunes(localTunes);
    }).catch(err => {
      console.warn('Dexie tune load failed:', err);
    });
    return;
  }

  const tunesRef = collection(db, 'users', user.uid, 'tunes');
  const unsubscribe = onSnapshot(tunesRef, snapshot => {
    const all = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    setTunes(all);
    dexie.tunes.bulkPut(all).catch(err => {
      console.warn('Dexie tune cache failed:', err);
    });
  });

  return () => unsubscribe();
}, [user]);

useEffect(() => {
  const updateHeight = () => {
    if (boxRef.current) {
      const boxHeight = boxRef.current.offsetHeight;
      const available = window.innerHeight - boxHeight - 40;
      setScrollHeight(Math.max(100, available));
    }
  };

  updateHeight();
  const handleResize = () => updateHeight();
  window.addEventListener('resize', handleResize);

  const resizeObserver = new ResizeObserver(() => updateHeight());
  if (boxRef.current) resizeObserver.observe(boxRef.current);

  return () => {
    window.removeEventListener('resize', handleResize);
    resizeObserver.disconnect();
  };
}, [collapsed]);

useEffect(() => {
  if (!user) return;

  const loadFilters = async () => {
    // First: try localStorage
    const saved = localStorage.getItem('setsFilters');
    if (saved) {
      try {
        const f = JSON.parse(saved);
        setFilterSetQuery(f.filterSetQuery || '');
        setFilterSetRating(f.filterSetRating || '');
        setFilterSetGenre(f.filterSetGenre || '');
        setFilterSetType(f.filterSetType || '');
        setSelectedSetTags(Array.isArray(f.selectedSetTags) ? f.selectedSetTags : []);
        setSetTagLogic(f.setTagLogic || 'AND');
        setSortOrder(f.sortOrder || 'newest');
        return;
      } catch {}
    }

    // Then: fallback to Firebase
    const ref = doc(db, 'users', user.uid, 'preferences', 'settings');
    const snap = await getDoc(ref);
    if (snap.exists()) {
      const f = snap.data().setsFilters || {};
      setFilterSetQuery(f.filterSetQuery || '');
      setFilterSetRating(f.filterSetRating || '');
      setFilterSetGenre(f.filterSetGenre || '');
      setFilterSetType(f.filterSetType || '');
      setSelectedSetTags(Array.isArray(f.selectedSetTags) ? f.selectedSetTags : []);
      setSetTagLogic(f.setTagLogic || 'AND');
      setSortOrder(f.sortOrder || 'newest');
    }
  };

  loadFilters();
}, [user]);

useEffect(() => {
  const saveFilters = () => {
    const filtersToSave = {
      filterSetQuery,
      filterSetRating,
      filterSetGenre,
      filterSetType,
      selectedSetTags,
      setTagLogic,
      sortOrder,
    };
    localStorage.setItem('setsFilters', JSON.stringify(filtersToSave));
  };

  window.addEventListener('beforeunload', saveFilters);
  return () => window.removeEventListener('beforeunload', saveFilters);
}, [
  filterSetQuery,
  filterSetRating,
  filterSetGenre,
  filterSetType,
  selectedSetTags,
  setTagLogic,
  sortOrder
]);



  // Fetch label options
  useEffect(() => {
    const fetchLabels = async () => {
      if (!user) return;
      const ref = doc(db, 'users', user.uid, 'preferences', 'settings');
      const snap = await getDoc(ref);
      if (snap.exists()) {
        const data = snap.data();
        const custom = Array.isArray(data.customRatingLabels) ? data.customRatingLabels : ['Awesome', 'Meh', 'To edit'];
        setLabelOptions(custom.filter(l => l !== '—'));
      } else {
        setLabelOptions(['Awesome', 'Meh', 'To edit']);
      }
    };
    fetchLabels();
  }, [user]);

useEffect(() => {
  if (!user) return;

  if (!navigator.onLine) {
    dexie.sets.toArray().then(localSets => {
      setSets(localSets);
    }).catch(err => {
      console.warn('Dexie set load failed:', err);
    });
    return;
  }

  const setsRef = collection(db, 'users', user.uid, 'sets');
  const tunesRef = collection(db, 'users', user.uid, 'tunes');

  const unsubscribe = onSnapshot(setsRef, async snapshot => {
    const rawSets = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

    const tuneIds = new Set();
    rawSets.forEach(set => {
      (set.tunes || []).forEach(t => {
        if (t?.id) tuneIds.add(t.id);
      });
    });

    const allTuneDocs = await getDocs(tunesRef);
    const tuneMap = {};
    allTuneDocs.forEach(doc => {
      tuneMap[doc.id] = { id: doc.id, ...doc.data() };
    });

    const hydratedSets = rawSets.map(set => {
      const hydratedTunes = (set.tunes || []).map(t => {
        const full = tuneMap[t.id];
        return full ? { ...full } : { ...t, missing: true };
      });
      return {
        ...set,
        tunes: hydratedTunes
      };
    });

    setSets(hydratedSets);
    dexie.sets.bulkPut(hydratedSets).catch(err =>
      console.warn('Dexie set caching failed:', err)
    );
  });

  return () => unsubscribe();
}, [user]);


useEffect(() => {
  if (!user) return;

  const filtersToSave = {
    filterSetQuery,
    filterSetRating,
    filterSetGenre,
    filterSetType,
    selectedSetTags,
    setTagLogic,
    sortOrder,
  };

  localStorage.setItem('setsFilters', JSON.stringify(filtersToSave));

  const ref = doc(db, 'users', user.uid, 'preferences', 'settings');
  setDoc(ref, { setsFilters: filtersToSave }, { merge: true });
}, [
  user,
  filterSetQuery,
  filterSetRating,
  filterSetGenre,
  filterSetType,
  selectedSetTags,
  setTagLogic,
  sortOrder
]);


  const toggleSetSelection = (id) => {
    setSelectedSetIds(prev =>
      prev.includes(id) ? prev.filter(sid => sid !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedSetIds.length === sets.length) {
      setSelectedSetIds([]);
    } else {
      setSelectedSetIds(sets.map(s => s.id));
    }
  };

  const deleteSet = async (id) => {
  let data = null;

  if (navigator.onLine) {
    const ref = doc(db, 'users', user.uid, 'sets', id);
    const snap = await getDoc(ref);
    data = snap.exists() ? snap.data() : null;
    await deleteDoc(ref);
  } else {
    data = await dexie.sets.get(id);
    await dexie.sets.delete(id);
    await dexie.pendingWrites.put({
      id: `${id}__delete`,
      type: 'delete',
      collection: 'sets',
      docId: id,
      data: {}
    });
  }

  return { id, data };
};

  const deleteSelectedSets = async () => {
  if (!user || selectedSetIds.length === 0) return;

  const confirmDelete = window.confirm(`Delete ${selectedSetIds.length} set(s)?`);
  if (!confirmDelete) return;

  const setsToDelete = [];

  for (const setId of selectedSetIds) {
    let data = null;

    if (navigator.onLine) {
      const ref = doc(db, 'users', user.uid, 'sets', setId);
      const snap = await getDoc(ref);
      data = snap.exists() ? snap.data() : null;
      await deleteDoc(ref);
    } else {
      data = await dexie.sets.get(setId);
      await dexie.sets.delete(setId);
      await dexie.pendingWrites.put({
        id: `${setId}__delete`,
        type: 'delete',
        collection: 'sets',
        docId: setId,
        data: {}
      });
    }

    if (data) {
      setsToDelete.push({ id: setId, data });
    }
  }

  setPendingDelete(setsToDelete);
  setSelectedSetIds([]);

  const timer = setTimeout(() => {
    setPendingDelete(null);
    setUndoTimer(null);
  }, 5000);

  setUndoTimer(timer);
};

const undoDelete = async () => {
  if (!user || !pendingDelete) return;
  clearTimeout(undoTimer);

for (const { id, data } of pendingDelete) {
  const ref = doc(db, 'users', user.uid, 'sets', id);

  await dexie.sets.put({ ...data, id }); // Always update Dexie

  await dexie.pendingWrites.put({
    id: `${id}__undo`,
    type: 'add',
    collection: 'sets',
    docId: id,
    data: { ...data, id }
  });

  if (navigator.onLine) {
    try {
      await setDoc(ref, data);
      await dexie.pendingWrites.delete(`${id}__undo`);
    } catch (err) {
      console.warn(`Failed to restore set ${id} online; will retry`, err);
    }
  }
}


  setPendingDelete(null);
  setUndoTimer(null);
};




 const getFilteredSets = () => {
  const matches = [];

  for (const set of sets) {
    const q = filterSetQuery.toLowerCase();

let matchedTuneTitles = [];

if (q) {
  (set.tunes || []).forEach(t => {
    if (t.title?.toLowerCase().includes(q)) {
      matchedTuneTitles.push(t.title);
    }
  });
}


    const matchesSearch =
      q === '' ||
      [set.title, set.notes, set.learnedFrom]
        .filter(Boolean)
        .some(field => field.toLowerCase().includes(q)) ||
      matchedTuneTitles.length > 0;

    const matchesGenre =
      !filterSetGenre || (set.tunes || []).some(t => (t.genre || '') === filterSetGenre);

    const matchesType =
      !filterSetType || (set.tunes || []).some(t => normalizeType(t.type) === normalizeType(filterSetType));

    const matchesRating =
      !filterSetRating ||
      (filterSetRating === '—'
        ? !set.rating || set.rating === ''
        : set.rating === filterSetRating);

    const matchesLabel = !filterSetLabel || set.ratingLabel === filterSetLabel;

    const matchesTags =
      selectedSetTags.length === 0 ||
      (setTagLogic === 'AND'
        ? selectedSetTags.every(tag => (set.customTags || []).includes(tag))
        : selectedSetTags.some(tag => (set.customTags || []).includes(tag)));

    if (
      matchesSearch &&
      matchesRating &&
      matchesLabel &&
      matchesGenre &&
      matchesType &&
      matchesTags
    ) {
      matches.push({ ...set, _matchedTuneTitles: matchedTuneTitles });
    }
  }

  return matches;
};



const filteredSets = getFilteredSets();

const ratingCounts = {};
const labelCounts = {};
const sourceCounts = {};

filteredSets.forEach(set => {
  const r = set.rating || '—';
  ratingCounts[r] = (ratingCounts[r] || 0) + 1;

  const l = set.ratingLabel || '—';
  labelCounts[l] = (labelCounts[l] || 0) + 1;


});



// Extract unique genre/type values from all sets
const allGenres = new Set();
const allTypes = new Set();

sets.forEach(set => {
  (set.tunes || []).forEach(t => {
    if (t.genre) allGenres.add(normalizeGenre(t.genre));
    if (t.type) allTypes.add(normalizeType(t.type));
  });
});

const genreOptions = [...allGenres].sort();
const typeOptions = [...allTypes].sort();

const getFilteredSubset = (ignoreField) => {
  return sets.filter(set => {
    const q = filterSetQuery.toLowerCase();

    const matchesSearch =
      q === '' ||
      [set.title, set.notes, set.learnedFrom]
        .filter(Boolean)
        .some(field => field.toLowerCase().includes(q)) ||
      (set.tunes || []).some(t => t.title?.toLowerCase().includes(q));

    const matchMap = {
      rating: !filterSetRating || (filterSetRating === '—' ? !set.rating : set.rating === filterSetRating),
      ratingLabel: !filterSetLabel || set.ratingLabel === filterSetLabel,
      genre: !filterSetGenre || (set.tunes || []).some(t => normalizeGenre(t.genre) === filterSetGenre),
      type: !filterSetType || (set.tunes || []).some(t => normalizeType(t.type) === normalizeType(filterSetType)),
    };

    delete matchMap[ignoreField];
    const restMatch = Object.values(matchMap).every(Boolean);
    return matchesSearch && restMatch;
  });
};

const genreCounts = {};
getFilteredSubset("genre").forEach(set => {
  const genreSet = new Set();
  (set.tunes || []).forEach(t => {
    const g = t.genre;
    if (g) genreSet.add(g);
  });
  genreSet.forEach(g => {
    genreCounts[g] = (genreCounts[g] || 0) + 1;
  });
});


    const typeCounts = {};
    getFilteredSubset("type").forEach(set => {
      const typeSet = new Set();
      (set.tunes || []).forEach(t => {
        const tNorm = normalizeType(t.type);
        if (tNorm) typeSet.add(tNorm);
      });
      typeSet.forEach(t => {
        typeCounts[t] = (typeCounts[t] || 0) + 1;
      });
    });

const sortedSets = useMemo(() => {
  const copy = [...filteredSets];

  if (sortOrder === 'alpha') {
    copy.sort((a, b) => a.title.localeCompare(b.title));
  } else {
    const getTime = (set) => {
      if (!set.createdAt) return 0;
      if (typeof set.createdAt === 'string') return Date.parse(set.createdAt) || 0;
      if (typeof set.createdAt.toMillis === 'function') return set.createdAt.toMillis();
      if (typeof set.createdAt.toDate === 'function') return set.createdAt.toDate().getTime();
      return 0;
    };

    copy.sort((a, b) =>
      sortOrder === 'oldest' ? getTime(a) - getTime(b) : getTime(b) - getTime(a)
    );
  }

  return copy;
}, [filteredSets, sortOrder]);

const handleBulkTagConfirm = async () => {
  if (!user || selectedSetIds.length === 0 || bulkTags.length === 0) return;

  const confirmed = window.confirm(`Add tag(s) to ${selectedSetIds.length} set(s)?`);
  if (!confirmed) return;

  try {
    for (const id of selectedSetIds) {
      let existing = [];
      let finalTags = [];

      if (navigator.onLine) {
        const ref = doc(db, 'users', user.uid, 'sets', id);
        const snap = await getDoc(ref);
        existing = snap.exists() ? snap.data().customTags || [] : [];
        finalTags = Array.from(new Set([...existing, ...bulkTags]));
        await updateDoc(ref, { customTags: finalTags });
      } else {
        const local = await dexie.sets.get(id);
        existing = local?.customTags || [];
        finalTags = Array.from(new Set([...existing, ...bulkTags]));
        await dexie.sets.update(id, { customTags: finalTags });

        await dexie.pendingWrites.put({
          id: `${id}__bulkTag`,
          type: 'update',
          collection: 'sets',
          docId: id,
          data: { customTags: finalTags }
        });
      }
    }

    setShowBulkTagInput(false);
    setBulkTags([]);
    setSelectedSetIds([]);
  } catch (err) {
    console.error('Failed to update tags on sets:', err);
    alert('Failed to apply tags.');
  }
};



  return (
    <PageWrapper>

        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>


<BorderedBox
  ref={boxRef}
  collapsible
  exposeCollapsed={setCollapsed}
  style={{ marginBottom: '0.7rem' }}
>

  <div className="app-title">
    <img src={myIcon} alt="Logo" className="responsive-icon" />
    <span>AllMySets</span>
  </div>

 <div style={{ maxWidth: '600px', margin: '0 auto' }}>

<div
  style={{
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
    gap: '0.7rem',
    maxWidth: '750px',
    margin: '0 auto 1rem auto',
    alignItems: 'center',
  }}
>
      <input
        className="search-input"
        placeholder="Search title or any metadata..."
        value={filterSetQuery}
        onChange={(e) => setFilterSetQuery(e.target.value)}
        style={{ ...inputStyle, gridColumn: '1 / -1' }}
      />



      <select
        style={{ ...inputStyle, minWidth: 0 }}
        className={filterSetGenre ? "filter-active" : ""}
        value={filterSetGenre}
        onChange={(e) => setFilterSetGenre(e.target.value)}
      >
        <option value="">All Genres</option>
          {genreOptions.map((g, i) => (
            <option key={i} value={g}>
              {g} ({genreCounts[g] || 0})
            </option>
          ))}
      </select>



      <select
        style={{ ...inputStyle, minWidth: 0 }}
        className={filterSetType ? "filter-active" : ""}
        value={filterSetType}
        onChange={(e) => setFilterSetType(e.target.value)}
      >
        <option value="">All Types</option>
          {typeOptions.map((t, i) => (
            <option key={i} value={t}>
              {t.charAt(0).toUpperCase() + t.slice(1)} ({typeCounts[t] || 0})
            </option>
          ))}
      </select>

      <select
        style={{ ...inputStyle, minWidth: 0 }}
        className={filterSetRating ? "filter-active" : ""}
        value={filterSetRating}
        onChange={(e) => setFilterSetRating(e.target.value)}
      >
        <option value="">All Ratings</option>
        <option value="—">No Rating ({ratingCounts['—'] || 0})</option>
        {['★', '★★', '★★★', '★★★★', '★★★★★'].map(r => (
          <option key={r} value={r}>
            {r} ({ratingCounts[r] || 0})
          </option>
        ))}
      </select>

   
  <button
    onClick={() => setShowSetTagFilters(v => !v)}
    style={{
      ...inputStyle,
      backgroundColor: selectedSetTags.length > 0 ? '#228be6' : '#444',
      color: 'white',
    }}
  >
    {showSetTagFilters
      ? `✖ Hide Tags (${selectedSetTags.length})`
      : `🏷️ Filter by Tags`}
  </button>

  <button
    title="Jump to a random set from the current filter"

    onClick={() => {
      
      const matching = getFilteredSets();
      if (matching.length === 0) return;
      const random = matching[Math.floor(Math.random() * matching.length)];
      navigate(`/sets/${random.id}`);
    }}
    style={{
      ...inputStyle,
      backgroundColor: '#444',
      color: 'white',
    }}
  >
    🎲 View Random Set!
  </button>

  <button
    onClick={() => {
      setFilterSetQuery('');
      setFilterSetRating('');
      setFilterSetGenre('');
      setFilterSetType('');
      setSelectedSetTags([]);
      setSetTagLogic('AND');
      setShowSetTagFilters(false);
    }}
    style={{
      ...inputStyle,
      backgroundColor: '#444',
      color: 'white',
    }}
  >
    🚫 Clear Filters
  </button>
</div>


{showSetTagFilters && prefs?.customTags?.length > 0 && (
  <div
    style={{
      display: 'grid',
      gridTemplateColumns: '1fr 1fr 1fr',
      gap: '0.75rem',
      maxWidth: '1000px',
      margin: '0 auto 1rem auto',
    }}
  >
    {/* Column 1: input + logic pills + sort pills (inside TagFilterControl) */}
    {/* Column 2–3: tag pills (inside TagFilterControl) */}
    <TagFilterControl
      availableTags={prefs.customTags}
      selectedTags={selectedSetTags}
      setSelectedTags={setSelectedSetTags}
      tagLogic={setTagLogic}
      setTagLogic={setSetTagLogic}
    />
  </div>
)}

<div
  style={{
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
    gap: '0.7rem',
    maxWidth: '750px',
    margin: '0 auto 1.25rem auto',
    alignItems: 'center',
  }}
>

  {/* Sort Order */}
  <select
    style={inputStyle}
    value={sortOrder}
    onChange={(e) => setSortOrder(e.target.value)}
  >
    <option value="newest">Newest first</option>
    <option value="oldest">Oldest first</option>
    <option value="alpha">A–Z</option>
  </select>

  {/* Select All / Deselect All */}
  <button
    onClick={toggleSelectAll}
    style={{ ...inputStyle, backgroundColor: '#444', color: 'white' }}
  >
    {selectedSetIds.length === filteredSets.length ? 'Deselect All' : 'Select All'}
  </button>

  {/* Bulk Actions */}
 <select
  style={inputStyle}
  value={bulkActionValue}
  onChange={async (e) => {
  const value = e.target.value;
  if (!value || selectedSetIds.length === 0) return;

  if (value === 'action:delete') {
    const confirmed = window.confirm(
      `Are you sure you want to delete ${selectedSetIds.length} set${selectedSetIds.length > 1 ? 's' : ''}?`
    );
    if (!confirmed) return;

    const deletedSets = [];
    for (const id of selectedSetIds) {
      const result = await deleteSet(id);
      if (result.data) deletedSets.push(result);
    }

    setUndoQueue(deletedSets);
    setSnackbarMessage(`Deleted ${deletedSets.length} set${deletedSets.length !== 1 ? 's' : ''}.`);
    setShowSnackbar(true);
    setTimeout(() => setShowSnackbar(false), 6000);
    setSelectedSetIds([]);
  }

  else if (value === 'action:addtags') {
    setShowBulkTagInput(true);
    return;
  }

  else if (value.startsWith('rating:')) {
    const newRating = value.split(':')[1];
    const confirmText = newRating
      ? `Set rating to "${newRating}"`
      : `Remove rating`;

    const confirmed = window.confirm(
      `${confirmText} for ${selectedSetIds.length} set${selectedSetIds.length > 1 ? 's' : ''}?`
    );
    if (!confirmed) return;

    for (const id of selectedSetIds) {
      const ref = doc(db, 'users', user.uid, 'sets', id);
      await updateDoc(ref, { rating: newRating || null });
    }

    setSelectedSetIds([]);
  }

  // ✅ Only clear selection after handling
  setBulkActionValue('');
}}

>
  <option value="">
    {selectedSetIds.length > 0
      ? `Bulk Actions on ${selectedSetIds.length} set${selectedSetIds.length > 1 ? 's' : ''}`
      : 'Bulk Actions…'}
  </option>
  <optgroup label="Actions">
    <option value="action:delete">🗑️ Delete Selected</option>
    <option value="action:addtags">🏷️ Add Tags to Selected</option>
  </optgroup>
  <optgroup label="Rating">
    <option value="rating:★">★</option>
    <option value="rating:★★">★★</option>
    <option value="rating:★★★">★★★</option>
    <option value="rating:★★★★">★★★★</option>
    <option value="rating:★★★★★">★★★★★</option>
    <option value="rating:">Remove Rating</option>
  </optgroup>
</select>

</div>

{showBulkTagInput && (
  <div
    style={{
      marginTop: '0.5rem',
      padding: '0.5rem',
      borderTop: '1px solid var(--border)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    }}
  >
    <p style={{ fontSize: '0.85rem', marginBottom: '0.5rem' }}>
      Add tags to selected sets:
    </p>

    <div
      style={{
        width: '100%',
        maxWidth: '400px',
        boxSizing: 'border-box',
        padding: '0 1rem',
      }}
    >
      <CustomTagInput
        tags={bulkTags}
        setTags={setBulkTags}
        suggestions={prefs?.customTags || []}
        inputStyle={inputStyle}
      />
    </div>

    <div style={{ marginTop: '0.75rem', display: 'flex', gap: '1rem' }}>
      <button
        onClick={handleBulkTagConfirm}
        style={{
          padding: '6px 12px',
          fontSize: '0.8rem',
          borderRadius: '6px',
          backgroundColor: '#228be6',
          color: 'white'
        }}
      >
        ✅ Confirm Tags
      </button>

      <button
        onClick={() => {
          setShowBulkTagInput(false);
          setBulkTags([]);
        }}
        style={{ fontSize: '0.8rem' }}
      >
        Cancel
      </button>
    </div>
  </div>
)}
<div style={{ display: 'flex', justifyContent: 'center', gap: '0.75rem', marginTop: '1rem' }}>
  <button
    onClick={async () => {
      if (!user) return;
      const ref = await addDoc(collection(db, 'users', user.uid, 'sets'), {
        title: '',
        notes: '',
        rating: '',
        ratingLabel: '',
        learnedFrom: '',
        tunes: [],
        createdAt: new Date().toISOString()
      });
      navigate(`/sets/${ref.id}/edit`);
    }}
    style={inputStyle}
  >
    + New Set
  </button>

  <button
    onClick={() => setShowIrishForm(v => !v)}
    style={inputStyle}
  >
    {showIrishForm ? '✖ Cancel' : '🎻 Irish Set Generator'}
  </button>
</div>

<CreateIrishSetForm
  user={user}
  inputStyle={inputStyle}
  tunes={tunes}
  showForm={showIrishForm}
  setShowForm={setShowIrishForm}
/>



    </div>


 
</BorderedBox>


</div>



{sets.length === 0 ? (
  <p>No sets yet.</p>
) : (
<div
  style={{
    overflowY: 'auto',
    paddingLeft: 0,
    paddingRight: 0,
    marginBottom: '2rem',
    boxSizing: 'border-box',
    height: `calc(100vh - ${collapsed ? 200 : 300}px)`
  }}
>

{showSnackbar && (
  <div
    style={{
      backgroundColor: '#444',
      color: 'white',
      padding: '8px 12px',
      margin: '10px auto',
      borderRadius: '4px',
      maxWidth: '600px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    }}
  >
    <span>{snackbarMessage}</span>
    <button
 onClick={async () => {
  for (const { id, data } of undoQueue) {
    await dexie.sets.put({ ...data, id });
    await dexie.pendingWrites.put({
      id: `${id}__undo`,
      type: 'add',
      collection: 'sets',
      docId: id,
      data: { ...data, id }
    });

    if (navigator.onLine) {
      try {
        const ref = doc(db, 'users', user.uid, 'sets', id);
        await setDoc(ref, data);
        await dexie.pendingWrites.delete(`${id}__undo`);
      } catch (err) {
        console.warn(`Undo sync failed for ${id}`, err);
      }
    }
  }
  setShowSnackbar(false);
}}

      style={{
        marginLeft: '20px',
        background: 'none',
        border: 'none',
        color: '#4dabf7',
        cursor: 'pointer',
        textDecoration: 'underline',
        fontSize: '0.9rem'
      }}
    >
      Undo
    </button>
  </div>
)}
<div
  style={{
    overflowY: 'auto',
    paddingLeft: 0,
    paddingRight: 0,
    marginBottom: '2rem',
    boxSizing: 'border-box',
    height: `${scrollHeight}px`
  }}
>
    <ul className="card-list">
      {sortedSets.map(set => (
        <TuneCardBox
          key={set.id}
          onClick={() => navigate(`/sets/${set.id}`)}
        >
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '1rem',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                flex: '1 1 200px',
                minWidth: '160px',
              }}
            >
            <div style={{ padding: '0.5rem' }}>
              <StyledCheckbox
                checked={selectedSetIds.includes(set.id)}
                onChange={() => toggleSetSelection(set.id)}
                onClick={(e) => e.stopPropagation()}
              />
            </div>


              <div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'baseline',
                    flexWrap: 'wrap',
                    gap: '0.5ch',
                  }}
                >
                  <strong style={{ fontSize: '1rem' }}>{set.title}</strong>
                  <span style={{ fontSize: '1rem', color: 'var(--text-secondary)' }}>
                    — {set.tunes?.length || 0} tune
                    {(set.tunes?.length || 0) !== 1 ? 's' : ''}
                  </span>
                </div>

                <div
                  style={{
                    fontSize: '0.9rem',
                    color: 'var(--text-muted)',
                    marginTop: '2px',
                  }}
                >
                  {[set.rating, set.learnedFrom]
                    .filter(Boolean)
                    .map((val, i) => (
                      <span
                        key={i}
                        style={{
                          marginRight: '10px',
                          color: i === 0 ? '#cc9900' : i === 1 ? '#666' : '#444',
                          fontStyle: i === 1 ? 'italic' : 'normal',
                        }}
                      >
                        {i === 2 ? `[from ${val}]` : val}
                      </span>
                    ))}
                </div>

{set.tunes?.length > 0 && (
  <div className="text-scaled" style={{ marginTop: '4px' }}>
    {set.tunes.map((t, i) => (
      <span key={i}>
{set._matchedTuneTitles?.length > 0 && set._matchedTuneTitles.includes(t.title) ? (
  <strong>{t.title}</strong>
) : (
  t.title
)}



        {i < set.tunes.length - 1 && ', '}
      </span>
    ))}
  </div>
)}

              </div>
            </div>
          </div>
        </TuneCardBox>
      ))}
    </ul>
    </div>
  </div>
)}

    </PageWrapper>
  );
}
